import { observer, useLocalObservable } from 'mobx-react';
import { useStore } from '../../../store/store';
import useHistory from '../../../hooks/useHistory';
import { paths } from '../../../utils/constants/routes';
import {
  encodeProjectsParams,
  isResultsTableTab,
  SELECTED_PROJECT_TABS,
  selectedProjectTabPageSize
} from '../constants';
import {
  buildProjectViewTabLabels,
  ProjectTabLabelText
} from '../../Projects/utils/projectViewTabLabels';
import {
  PROFILER_INDUSTRY_CODES_SEARCH_FIELDS,
  PROFILER_SEARCH_FIELDS
} from '../../../utils/constants/fields';
import { useEffect, useState } from 'react';
import { API_ENDPOINTS } from '../../../api/endpoints';
import GeneralLoader from '../../../components/loaders/GeneralLoader';
import {
  PROFILER_INDUSTRY_CODES_SEARCH_CATEGORIES,
  PROFILER_SEARCH_CATEGORIES
} from '../../../utils/constants/searchCategories';
import { formatNumberRange } from '../../../utils/utils';
import { dataSourcesMoreAction } from '../../../components/common/dataSourcesMoreAction';
import GeneralError from '../../../components/errors/GeneralError';
import ProfilerSearchResultsTabView from './ProfilerSearchResultsTabView';
import IconButton, {
  ICON_BUTTON_TYPES,
  ICON_BUTTON_ICONS
} from '../../../components/buttons/IconButton';
import { Link } from 'react-router';
import FeedbackPopup from './FeedbackPopup';
import ProfilerCommentsTabView from './ProfilerCommentsTabView';
import { isBulkSearchProject, bulkSearchCriteriaText } from './bulkSearchCriteria';
import { hideBulkMatchBadgeRows } from '../../../utils/profiler/hideBulkMatchBadge';
import pubSub from '../../../utils/pubsub';
import { runInAction } from 'mobx';
import CompanyDetails from '../../../components/profiler/CompanyDetails/CompanyDetails';
import { UI_OPTIONS } from '../../../utils/constants/uiOptions';
import { UI_OPTION_KEYS } from '../../../utils/constants/uiOptionKeys';
import { HEADER_MESSAGE_TYPES } from '../../../utils/constants/header';
import ChipsList from '../../../components/common/ChipsList';
import {
  isProfilerAdvancedCriteriaDisabled,
  isProfilerAdvancedCriteriaSituationDisabled,
  isProfilerSearchDownloadDisabled,
  isUnifiedFiltersEnabled
} from '../../../utils/constants/variables';
import useElementHeightCssVar from '../../../hooks/useElementHeightCssvar';
import { PUNCTUATION } from '../../../utils/constants/notation';
import { useCompanyNavigationStack } from '../../../hooks/useCompanyNavigationStack';
import useProjectDownloads from '../../../hooks/useProjectDownloads';
import { isAnyProfilerDownloadInFlight } from '../../../utils/profilerDownloadHelpers';
import ProjectDownloadControls, {
  downloadStateIcon
} from '../../../components/profiler/ProjectDownload/ProjectDownloadControls';
import ProjectDownloadOverlays from '../../../components/profiler/ProjectDownload/ProjectDownloadOverlays';
import DataSourceToggleIcon from '../../../components/common/DataSourceToggleIcon';
import ResultsMobileActionBar from '../../../components/common/ResultsMobileActionBar';
import DataSourceFloatingLegend from '../../../components/common/DataSourceFloatingLegend';
import OwnershipCriteriaGroup from '../../../components/profiler/ownership/OwnershipCriteriaGroup';

const ProfilerProjectView = observer(({ project = {}, params = {}, isFromAgentView = false }) => {
  const store = useStore();
  const { profilerProjectStore, utilsStore, authStore, profilerAccessStore } = store;
  const { navigate } = useHistory();
  const navStack = useCompanyNavigationStack({ scrollKey: project?.id });

  // Harmonized toolbar mount (flag-on desktop): the bar lives ABOVE the tab
  // strip but is rendered by the child results view, which owns the filter /
  // view / sort state. We render an empty anchor above the tabs and hand its
  // node to the child so it can portal the bar in. Callback ref → state keeps
  // the node stable across Results/Excluded tab switches.
  const [toolbarMountNode, setToolbarMountNode] = useState(null);
  const state = useLocalObservable(() => ({
    isRendered: true,
    unrender: () => (state.isRendered = false),
    project: project,
    isLoading: false,
    loadError: false,
    results: [],
    fullResultsCount: 0,
    get nonExcludedResultsCount() {
      return state.results.filter((r) => !r.isExcluded).length;
    },
    // V2.0 — count of rows the analyst actually sees in the List view after
    // Spotlights + column filters are applied. The TabView pushes this up
    // via the `onFilteredCountChange` callback whenever filters change.
    // Defaults to `null` (no filters active) so the tab label can fall back
    // to the plain "Search results (N)" format when nothing's filtered.
    filteredResultsCount: null,
    setFilteredResultsCount: (count) => {
      state.filteredResultsCount = count;
    },
    // The category is passed by the caller from the current `project` prop rather than read
    // from `state.project`, which is captured once and goes stale when the view switches
    // projects without remounting.
    loadResults: (projectId = null, searchCategory = null) => {
      if (state.isLoading || !projectId) {
        return;
      }

      state.loadError = false;
      state.isLoading = true;
      store.makeRequest({
        endpoint: API_ENDPOINTS.GET_PROFILER_PROJECT_RESULTS,
        body: { projectId },
        onSuccess: (response) => {
          if (!state.isRendered) {
            return;
          }

          // Bulk rows never show the AI match badge: their score is the name matcher's similarity.
          if (isBulkSearchProject(searchCategory)) {
            hideBulkMatchBadgeRows(response.results);
          }

          state.results = profilerAccessStore.isProfilerDemo
            ? response.results.slice(0, selectedProjectTabPageSize)
            : response.results;
          state.fullResultsCount = response.totalCount || response.results.length;
        },
        onError: (errorMessage) => {
          if (!state.isRendered) {
            return;
          }

          state.loadError = errorMessage || 'Failed to obtain project results.';
        },
        onFinally: () => {
          if (!state.isRendered) {
            return;
          }
          state.isLoading = false;
        }
      });
    },

    // remove when API_ENDPOINTS.EXCLUDE_PROFILER_PROJECT_RESULT is fixed [ START ]
    entriesInAction: {},
    excludeResultQueue: [],
    excludeResult: (company, exclude = true) => {
      const companyId = company?.id;
      if (state.entriesInAction[companyId] || !companyId) {
        return;
      }

      state.entriesInAction[companyId] = true;

      const task = () =>
        store.makeRequest({
          endpoint: API_ENDPOINTS.EXCLUDE_PROFILER_PROJECT_RESULT,
          body: { projectId: state.project.id, companyId, exclude: !!exclude },
          onSuccess: () => {
            if (!state.isRendered) {
              return;
            }
            const foundResult = state.results.find((r) => r.id === companyId);

            if (foundResult) {
              foundResult.isExcluded = !!exclude;
              // utilsStore.setHeaderMessage(
              //   `Result successfully moved to ${!exclude ? ' saved ' : ' excluded '} results.`
              // );
            }
          },
          onError: (errorMessage) => {
            if (!state.isRendered) {
              return;
            }

            utilsStore.setHeaderMessage(
              errorMessage || 'Failed to exclude result.',
              HEADER_MESSAGE_TYPES.ERROR
            );
          },
          onFinally: () => {
            if (!state.isRendered) {
              return;
            }
            state.entriesInAction[companyId] = false;
            state.dequeueExcludeResultTask();
          }
        });

      state.excludeResultQueue.push(task);
      if (state.excludeResultQueue.length === 1) {
        task();
      }
    },
    dequeueExcludeResultTask: () => {
      state.excludeResultQueue.shift();
      if (state.excludeResultQueue.length > 0) {
        state.excludeResultQueue[0]();
      }
    },
    // remove when API_ENDPOINTS.EXCLUDE_PROFILER_PROJECT_RESULT is fixed [  END  ]

    // TD-2461 × TD-2560 — bulk exclusion for the saved-project results view. The
    // child (ProfilerSearchResultsTabView) owns the filter state, computes the
    // matching / complement id sets, and drives the menu UI; the dispatch lives
    // here beside the single-row excludeResult (same endpoint, array form per
    // TD-2462). Returns a promise so the child can track its in-flight state.
    // Split across three store methods so the makeRequest callbacks stay within
    // the 4-level function-nesting budget (mirrors excludeResult's shape).
    bulkExcludeResults: (companyIds = []) =>
      new Promise((resolve) => {
        if (!companyIds.length) resolve();
        else state._sendBulkExclude(companyIds, resolve);
      }),
    _sendBulkExclude: (companyIds, resolve) => {
      store.makeRequest({
        endpoint: API_ENDPOINTS.EXCLUDE_PROFILER_PROJECT_RESULT,
        body: { projectId: state.project.id, companyIds, exclude: true },
        onSuccess: () => state._applyBulkExcluded(companyIds),
        onError: (errorMessage) => {
          if (!state.isRendered) return;
          utilsStore.setHeaderMessage(
            errorMessage || `Failed to exclude ${companyIds.length} companies.`,
            HEADER_MESSAGE_TYPES.ERROR
          );
        },
        onFinally: () => resolve()
      });
    },
    _applyBulkExcluded: (companyIds) => {
      if (!state.isRendered) return;
      const idSet = new Set(companyIds);
      state.results.forEach((r) => {
        if (idSet.has(r.id)) r.isExcluded = true;
      });
    },
    isRequestFeedbackPopupOpen: false,
    toggleRequestFeedbackPopup: () => {
      state.isRequestFeedbackPopupOpen = !state.isRequestFeedbackPopupOpen;
      if (state.isRequestFeedbackPopupOpen) {
        utilsStore.lockScroll(true);
      } else {
        utilsStore.lockScroll(false);
      }
    },
    isFieldsSectionCollapsed: true,
    toggleFieldsSection: () => {
      state.isFieldsSectionCollapsed = !state.isFieldsSectionCollapsed;
      if (!state.isFieldsSectionCollapsed) {
        setTimeout(() => {
          if (!state.isFieldsSectionCollapsed) {
            const mandatoryContainerDOM = document.getElementById('pv-mandatory-container');
            if (mandatoryContainerDOM) {
              window.scrollTo({
                top: mandatoryContainerDOM.offsetTop - (state.layout === 'mobile' ? 52 : 80),
                behavior: 'smooth'
              });
            }
          }
        }, 10);
      }
    },
    get layout() {
      return utilsStore.windowWidth < 1201 ? 'mobile' : 'desktop';
    }
  }));

  // TD-2970 — download popup state, fetch/save, bulk-export confirmation and the
  // contacts dialog live in the hook; the components below only render it.
  const downloads = useProjectDownloads({
    project: state.project,
    nonExcludedResultsCount: state.nonExcludedResultsCount,
    isLoadingResults: state.isLoading
  });

  useElementHeightCssVar('.tabs.at-project-results', '--project-view-tabs-height');
  useElementHeightCssVar('.header.at-project-results', '--project-view-header-height');
  // TD-2733 — the stacked heatmap's own chrome, so `.heatmap-wrap` can subtract it and
  // leave the top of the list on screen. Scoped to this page's results area so the
  // company-details overlay cannot match first; both self-zero in list and chart view,
  // which is what the calc needs.
  useElementHeightCssVar(
    '.project-details .lists .profiler-heatmap-section-header',
    '--profiler-heatmap-section-header-height'
  );
  useElementHeightCssVar(
    '.project-details .lists .profiler-heatmap-controls-header.mobile-view',
    '--profiler-heatmap-controls-mobile-height'
  );
  // The harmonized toolbar row is portaled above the tabs and is not sticky, so it is not
  // part of the resting sticky stack — but every view change scrolls back to the top of the
  // results section, which sits above it, so it is on screen exactly when the peek matters.
  useElementHeightCssVar('.pv-toolbar-portal-mount', '--project-view-toolbar-height');
  useElementHeightCssVar(
    '.project-details .lists .profiler-heatmap-section .heatmap-legend',
    '--heatmap-legend-height'
  );

  useEffect(() => {
    return () => {
      state.unrender();
    };
  }, [state]);

  useEffect(() => {
    runInAction(() => {
      state.project = project;
    });
  }, [state, project]);

  useEffect(() => {
    return () => {
      if (navStack.detailsDisplayed) {
        navStack.displayDetails(null);
      }
    };
  }, [navStack]);

  useEffect(() => {
    profilerProjectStore.updateProjectLastCheckedDate(project.id);

    const subscription = (payload) => {
      if (
        payload?.event === 'update-project-last-checked-date' &&
        payload?.projectId === project.id
      ) {
        setTimeout(() => {
          profilerProjectStore.updateProjectLastCheckedDate(project.id);
        }, 0);
      }
    };

    pubSub.subscribe(subscription);

    return () => {
      pubSub.unsubscribe(subscription);
    };
  }, [project.id, profilerProjectStore]);

  const scrollOnAction = (tabId = '') => {
    setTimeout(() => {
      const el = document.getElementById('project-view-scroll-starting-point');
      const header = document.querySelector('header');
      if (el) {
        const headerHeight = header?.clientHeight || 0;
        window.scrollTo({ top: el.offsetTop - headerHeight, behavior: 'smooth' });
      }
      setTimeout(() => {
        const tabDOM = document.getElementById(tabId);
        if (tabDOM) {
          tabDOM.scrollIntoView({ behavior: 'smooth', block: 'nearest', inline: 'center' });
        }
      }, 500);
    }, 100);
  };

  // V2.0 — when V1 filters narrow the visible set, show `X/Y` so the tab label conveys both
  // the filtered count and the total. Falls back to the plain total when no filters are
  // active (or when the child hasn't reported a count yet).
  const resultsCountLabel =
    state.filteredResultsCount != null &&
    state.filteredResultsCount !== state.nonExcludedResultsCount
      ? `${state.filteredResultsCount}/${state.nonExcludedResultsCount}`
      : String(state.nonExcludedResultsCount);
  // Mobile gets a shortened label. The three full labels need 483px of a 375px screen, and
  // while the strip scrolls, dropping the leading noun keeps all three within a thumb's reach
  // rather than parking "Team comments" off the right edge.
  const excludedCount = state.results.filter((r) => r.isExcluded).length;
  const tabLabels = buildProjectViewTabLabels({
    resultsCountLabel,
    excludedCount,
    teamCommentsCount: project.teamComments.length
  });

  const selectedProjectTabs = [
    {
      id: 'project-view-tab-1',
      name: tabLabels.results.name,
      mobileName: tabLabels.results.mobileName,
      onClick: () => {
        if (params.selectedProjectTab === SELECTED_PROJECT_TABS.EXCLUDED) {
          pubSub.publish({ event: 'resetProfilerHeatmapSavedState', searchId: project.searchId });
        }
        navigate(
          (isFromAgentView ? paths.PROJECTS : paths.PROFILER_PROJECTS) +
            '?params=' +
            encodeProjectsParams({
              ...params,
              selectedProjectTab: SELECTED_PROJECT_TABS.RESULTS,
              selectedProjectTabPage: 1
            })
        );
        scrollOnAction('project-view-tab-1');
      },
      isActive: params.selectedProjectTab === SELECTED_PROJECT_TABS.RESULTS
    },
    {
      id: 'project-view-tab-2',
      name: tabLabels.excluded.name,
      mobileName: tabLabels.excluded.mobileName,
      onClick: () => {
        if (params.selectedProjectTab === SELECTED_PROJECT_TABS.RESULTS) {
          pubSub.publish({ event: 'resetProfilerHeatmapSavedState', searchId: project.searchId });
        }
        navigate(
          (isFromAgentView ? paths.PROJECTS : paths.PROFILER_PROJECTS) +
            '?params=' +
            encodeProjectsParams({
              ...params,
              selectedProjectTab: SELECTED_PROJECT_TABS.EXCLUDED,
              selectedProjectTabPage: 1
            })
        );
        scrollOnAction('project-view-tab-2');
      },
      isActive: params.selectedProjectTab === SELECTED_PROJECT_TABS.EXCLUDED
    },
    {
      id: 'project-view-tab-3',
      name: tabLabels.comments.name,
      mobileName: tabLabels.comments.mobileName,
      onClick: () => {
        navigate(
          (isFromAgentView ? paths.PROJECTS : paths.PROFILER_PROJECTS) +
            '?params=' +
            encodeProjectsParams({
              ...params,
              selectedProjectTab: SELECTED_PROJECT_TABS.COMMENTS,
              selectedProjectTabPage: 1
            })
        );
        scrollOnAction('project-view-tab-3');
      },
      isActive: params.selectedProjectTab === SELECTED_PROJECT_TABS.COMMENTS
    }
  ];

  useEffect(() => {
    state.loadResults(project.id, project.searchCategory);
    authStore.loadCompanyUsers();
  }, [state, authStore, project.id, project.searchCategory]);

  useEffect(() => {
    return () => {
      if (state.isRequestFeedbackPopupOpen) {
        state.toggleRequestFeedbackPopup();
      }
    };
  }, [state]);

  useEffect(() => {
    if (state.isLoading) {
      return;
    }

    let shouldNavigate = false;
    const newParams = {};

    if (!Object.values(SELECTED_PROJECT_TABS).includes(params.selectedProjectTab)) {
      newParams.selectedProjectTab = SELECTED_PROJECT_TABS.RESULTS;
      shouldNavigate = true;
    }

    const list =
      params.selectedProjectTab === SELECTED_PROJECT_TABS.RESULTS
        ? state.results.filter((r) => !r.isExcluded)
        : params.selectedProjectTab === SELECTED_PROJECT_TABS.EXCLUDED
          ? state.results.filter((r) => r.isExcluded)
          : project.teamComments;
    let listPages = Math.ceil(list.length / selectedProjectTabPageSize);
    if (listPages === 0) {
      listPages = 1;
    }

    if (
      isNaN(params.selectedProjectTabPage) ||
      params.selectedProjectTabPage > listPages ||
      params.selectedProjectTabPage < 1
    ) {
      if (isNaN(params.selectedProjectTabPage) || params.selectedProjectTabPage < 1) {
        newParams.selectedProjectTabPage = 1;
      } else if (params.selectedProjectTabPage > listPages) {
        newParams.selectedProjectTabPage = listPages;
      }
      shouldNavigate = true;
    }

    if (shouldNavigate) {
      navigate(
        (isFromAgentView ? paths.PROJECTS : paths.PROFILER_PROJECTS) +
          '?params=' +
          encodeProjectsParams({ ...params, ...newParams }),
        { replace: true }
      );
    }
  }, [params, state.results, state.results.filter((r) => r.isExcluded).length]);

  const onBackClick = (ev) => {
    ev?.preventDefault?.();
    ev?.nativeEvent?.preventDefault?.();
    ev?.nativeEvent?.stopPropagation?.();
    const newParams = { ...params };
    newParams.selectedProjectId = null;
    newParams.selectedProjectTab = null;
    newParams.selectedProjectTabPage = null;
    navigate(
      (isFromAgentView ? paths.PROJECTS : paths.PROFILER_PROJECTS) +
        '?params=' +
        encodeProjectsParams(newParams),
      { replace: true }
    );
  };

  const showFeedbackPopup = !!state.isRequestFeedbackPopupOpen;
  // TD-2392 — the "Show data sources" toggle + floating legend only make sense
  // on the tabs that render the results table; hide them on Team comments.
  const showDataSourceControls = isResultsTableTab(params.selectedProjectTab);

  // TD-2575 — info / Request-feedback / download historically live in the page
  // header (desktop) + a mobile bottom bar. Under the unified-filter flag they
  // are relocated into the results toolbar (rendered by the child
  // <ProfilerSearchResultsTabView>) so Projects matches the Search-results bar.
  // TD-2970 — the download subsystem itself (state, hooks, popup, modals) lives
  // in useProjectDownloads + components/profiler/ProjectDownload; this helper
  // is the single source for all three placements (flag-off desktop, flag-off
  // mobile, flag-on toolbar).
  const renderProjectDownloadControls = ({ idProps = {}, onepagerTooltipPosition } = {}) => (
    <ProjectDownloadControls
      downloads={downloads}
      idProps={idProps}
      onepagerTooltipPosition={onepagerTooltipPosition}
    />
  );

  const renderRequestFeedbackButton = ({ icon, filled = true, wrapClassName } = {}) => (
    <IconButton
      type={ICON_BUTTON_TYPES.BLUE}
      filled={filled}
      innerText="Request feedback"
      onClick={state.toggleRequestFeedbackPopup}
      {...(icon ? { icon } : {})}
      {...(wrapClassName ? { wrapClassName } : {})}
    />
  );

  // Flag-on results tabs on DESKTOP: the harmonized toolbar (rendered by the
  // child) owns info/download/feedback, so the desktop page-header cluster is
  // hidden. On the Comments tab (no toolbar) or flag-off it shows as before. The
  // MOBILE bottom bar always stays as today — mobile harmonization into an
  // enrichment-style bottom action bar is a follow-up — so it is NOT gated here.
  const showDesktopHeaderActions =
    !isUnifiedFiltersEnabled || !isResultsTableTab(params.selectedProjectTab);
  // Outline (not filled) so it blends with the other toolbar buttons on the
  // harmonized desktop bar; the flag-off header/mobile CTA stays filled.
  const harmonizedFeedbackSlot = isUnifiedFiltersEnabled
    ? renderRequestFeedbackButton({
        icon: ICON_BUTTON_ICONS.CHAT_SQUARE_TEXT,
        filled: false,
        wrapClassName: 'pv-toolbar-text-button'
      })
    : null;
  const harmonizedTrailingActionsSlot = isUnifiedFiltersEnabled ? (
    <>
      {showDataSourceControls && (
        <DataSourceToggleIcon id="data-sources-btn-project" wrapClassName="filter-button-no-text" />
      )}
      <span className="pv-download-anchor">
        {renderProjectDownloadControls({
          idProps: { id: 'project-view-download-results-button-desktop' }
        })}
      </span>
    </>
  ) : null;

  // TD-2769: the mobile results bar lives in the results child
  // (<ProfilerSearchResultsTabView>) so its Add-filter / View / Sort / Customize
  // actions share the store that owns those toggles. The parent still owns the
  // project-level controls, and hands them down as descriptors rather than as a
  // node slot: the bar runs in descriptor mode, so it renders them as its own real
  // <button>s and nothing has to carry page-scoped styling through its portal.
  //
  // Download is the signed-off primary CTA. Its popup is too rich to be a bar item
  // (two credit counters and three entitlement-gated export buttons), so the
  // trigger is the CTA and the body opens as a MobileSheet, mirroring Search.
  // While an export runs the CTA shows the entries' in-progress file icon: the
  // sheet closes on selection, so the entry icons are off screen.
  const isExportInFlight = isAnyProfilerDownloadInFlight(downloads.state);
  const mobilePrimaryAction = {
    label: 'Download',
    icon: downloadStateIcon({ isDownloading: isExportInFlight }) ?? ICON_BUTTON_ICONS.DOWNLOAD,
    onClick: downloads.state.toggle,
    active: downloads.state.isOpen,
    busy: isExportInFlight,
    disabled: isProfilerSearchDownloadDisabled
  };

  const mobileMoreActions = [
    {
      key: 'request-feedback',
      label: 'Request feedback',
      icon: ICON_BUTTON_ICONS.CHAT_SQUARE_TEXT,
      onClick: state.toggleRequestFeedbackPopup
    },
    ...(showDataSourceControls ? [dataSourcesMoreAction(utilsStore)] : [])
  ];

  if (navStack.detailsDisplayed) {
    return (
      <>
        {/* TD-2392 — the CompanyDetails *component* (unlike the /company/:id
            page) doesn't mount its own legend, so the "Show data sources"
            header toggle would be inert when a company is opened from a
            project. Mount the legend here so it owns `body.show-data-sources`
            and the profile's headline colouring works. */}
        <DataSourceFloatingLegend />
        <CompanyDetails
          company={navStack.detailsDisplayed}
          displayDetails={navStack.displayDetails}
          goBackToPreviousCompany={navStack.goBackToPreviousCompany}
          companiesStackLength={navStack.companiesStack.length}
          savedTab={navStack.savedTabForCurrentCompany}
          savedComparablesId={navStack.savedComparablesIdForCurrentCompany}
          isBackNavigation={navStack.isBackNavigation}
          clearBackNavigationFlag={navStack.clearBackNavigationFlag}
          project={project}
        />
      </>
    );
  }

  return (
    <div className="project-details">
      {/* TD-2352 / TD-2392 — data-source headline colouring for the saved-project
          results view. The legend owns the `body.show-data-sources` class and the
          floating key; mounted once here (the company-details drill-in returns
          early above and mounts its own legend, so there's never a double mount).
          Gated to the results/excluded tabs — no company data to decode on the
          Team comments tab. */}
      {showDataSourceControls && <DataSourceFloatingLegend />}
      <Link
        to={
          (isFromAgentView ? paths.PROJECTS : paths.PROFILER_PROJECTS) +
          '?params=' +
          encodeProjectsParams({ ...params, selectedProjectId: null })
        }
        className="backlink"
        onClick={onBackClick}
      >
        Back to projects
      </Link>

      <div className="project-view-header">
        <div className="title">{project.name}</div>
        {showDesktopHeaderActions && (
          <div className="actions desktop-only">
            {showDataSourceControls && (
              <DataSourceToggleIcon id="data-sources-btn-project-desktop" />
            )}
            {renderProjectDownloadControls({
              idProps: { id: 'project-view-download-results-button-desktop' }
            })}
            {renderRequestFeedbackButton()}
          </div>
        )}
      </div>

      <section>
        <div className="mandatory-container" id="pv-mandatory-container">
          <div className="mandatory-header">
            <div className="section-title">Search criteria</div>
            <IconButton
              type={ICON_BUTTON_TYPES.DEFAULT}
              icon={
                state.isFieldsSectionCollapsed
                  ? ICON_BUTTON_ICONS.CHEVRON_DOWN
                  : ICON_BUTTON_ICONS.CHEVRON_UP
              }
              innerText={state.isFieldsSectionCollapsed ? 'Expand' : 'Collapse'}
              onClick={state.toggleFieldsSection}
              iconOnRight
              withBorder={false}
            />
          </div>
          {!state.isFieldsSectionCollapsed && (
            <div className="fields">
              <div className="group">
                <div className="field">
                  <div className="label">Profile</div>
                  <div className="value">Corporate</div>
                </div>
                <div className="field">
                  <div className="label">Search category</div>
                  <div className="value">
                    {Object.entries(UI_OPTIONS[UI_OPTION_KEYS.PROFILER_SEARCH_CATEGORY]).find(
                      (ntry) => ntry[1] === project.searchCategory
                    )?.[0] || project.searchCategory}
                  </div>
                </div>
                <div className="field empty"></div>
              </div>
              {project.searchCategory ===
                UI_OPTIONS[UI_OPTION_KEYS.PROFILER_SEARCH_CATEGORY]['Characteristics'] && (
                <>
                  <div className="group-title">General</div>
                  <div className="group">
                    <div className="field">
                      <div className="label">{PROFILER_SEARCH_FIELDS.INDUSTRY.LABEL}</div>
                      <div className="value">
                        {project.fields[PROFILER_SEARCH_FIELDS.INDUSTRY.NAME]}
                      </div>
                    </div>
                    <div className="field">
                      <div className="label">{PROFILER_SEARCH_FIELDS.VALUE_CHAIN.LABEL}</div>
                      <div className="value">
                        {project.fields[PROFILER_SEARCH_FIELDS.VALUE_CHAIN.NAME]}
                      </div>
                    </div>
                    <div className="field">
                      <div className="label">{PROFILER_SEARCH_FIELDS.PRODUCT_PORTFOLIO.LABEL}</div>
                      <div className="value">
                        {project.fields[PROFILER_SEARCH_FIELDS.PRODUCT_PORTFOLIO.NAME]}
                      </div>
                    </div>
                  </div>
                  <div className="group">
                    <div className="field">
                      <div className="label">{PROFILER_SEARCH_FIELDS.REVENUE_MIN.LABEL}</div>
                      <div className="value">
                        {formatNumberRange(
                          project.fields[PROFILER_SEARCH_FIELDS.REVENUE_MIN.NAME],
                          project.fields[PROFILER_SEARCH_FIELDS.REVENUE_MAX.NAME]
                        )}
                      </div>
                    </div>
                    <div className="field empty"></div>
                    <div className="field empty"></div>
                  </div>
                  <div className="group-title">
                    {PROFILER_SEARCH_CATEGORIES.ADVANCED_SEARCH_CRITERIA}
                  </div>
                  {isProfilerAdvancedCriteriaDisabled ? (
                    <>
                      <div className="group">
                        <div className="field">
                          <div className="label">{PROFILER_SEARCH_FIELDS.EMPLOYEES_MIN.LABEL}</div>
                          <div className="value">
                            {!project.fields[PROFILER_SEARCH_FIELDS.EMPLOYEES_MIN.NAME]
                              ? ''
                              : formatNumberRange(
                                  project.fields[PROFILER_SEARCH_FIELDS.EMPLOYEES_MIN.NAME],
                                  project.fields[PROFILER_SEARCH_FIELDS.EMPLOYEES_MAX.NAME]
                                )}
                          </div>
                        </div>
                        <div className="field">
                          <div className="label">{PROFILER_SEARCH_FIELDS.HEADQUARTER.LABEL}</div>
                          <div className="value">
                            {!project.fields[PROFILER_SEARCH_FIELDS.HEADQUARTER.NAME].length
                              ? ''
                              : utilsStore
                                  .getOptionName(project.fields, PROFILER_SEARCH_FIELDS.HEADQUARTER)
                                  .join(', ')}
                          </div>
                        </div>
                        <div className="field empty"></div>
                      </div>
                    </>
                  ) : (
                    <>
                      <div className="group">
                        <div className="field">
                          <div className="label">{PROFILER_SEARCH_FIELDS.CAGR_MIN.LABEL}</div>
                          <div className="value">
                            {!project.fields[PROFILER_SEARCH_FIELDS.CAGR_MIN.NAME] &&
                            !project.fields[PROFILER_SEARCH_FIELDS.CAGR_MAX.NAME]
                              ? ''
                              : formatNumberRange(
                                  project.fields[PROFILER_SEARCH_FIELDS.CAGR_MIN.NAME],
                                  project.fields[PROFILER_SEARCH_FIELDS.CAGR_MAX.NAME],
                                  PUNCTUATION.EN_DASH,
                                  true
                                )}
                          </div>
                        </div>
                        <div className="field">
                          <div className="label">{PROFILER_SEARCH_FIELDS.CAGR_PERIOD.LABEL}</div>
                          <div className="value">
                            {!project.fields[PROFILER_SEARCH_FIELDS.CAGR_PERIOD.NAME].length
                              ? ''
                              : utilsStore.getOptionName(
                                  project.fields,
                                  PROFILER_SEARCH_FIELDS.CAGR_PERIOD
                                )}
                          </div>
                        </div>
                        <div className="field">
                          <div className="label">
                            {PROFILER_SEARCH_FIELDS.EBIT_MARGIN_MIN.LABEL}
                          </div>
                          <div className="value">
                            {!project.fields[PROFILER_SEARCH_FIELDS.EBIT_MARGIN_MIN.NAME] &&
                            !project.fields[PROFILER_SEARCH_FIELDS.EBIT_MARGIN_MAX.NAME]
                              ? ''
                              : formatNumberRange(
                                  project.fields[PROFILER_SEARCH_FIELDS.EBIT_MARGIN_MIN.NAME],
                                  project.fields[PROFILER_SEARCH_FIELDS.EBIT_MARGIN_MAX.NAME],
                                  PUNCTUATION.EN_DASH,
                                  true
                                )}
                          </div>
                        </div>
                      </div>
                      <div className="group">
                        <div className="field">
                          <div className="label">{PROFILER_SEARCH_FIELDS.EMPLOYEES_MIN.LABEL}</div>
                          <div className="value">
                            {!project.fields[PROFILER_SEARCH_FIELDS.EMPLOYEES_MIN.NAME]
                              ? ''
                              : formatNumberRange(
                                  project.fields[PROFILER_SEARCH_FIELDS.EMPLOYEES_MIN.NAME],
                                  project.fields[PROFILER_SEARCH_FIELDS.EMPLOYEES_MAX.NAME]
                                )}
                          </div>
                        </div>
                        <div className="field">
                          <div className="label">{PROFILER_SEARCH_FIELDS.HEADQUARTER.LABEL}</div>
                          <div className="value">
                            {!project.fields[PROFILER_SEARCH_FIELDS.HEADQUARTER.NAME].length
                              ? ''
                              : utilsStore
                                  .getOptionName(project.fields, PROFILER_SEARCH_FIELDS.HEADQUARTER)
                                  .join(', ')}
                          </div>
                        </div>
                        {!isProfilerAdvancedCriteriaSituationDisabled && (
                          <div className="field">
                            <div className="label">{PROFILER_SEARCH_FIELDS.SITUATION.LABEL}</div>
                            <div className="value">
                              {!project.fields[PROFILER_SEARCH_FIELDS.SITUATION.NAME].length
                                ? ''
                                : utilsStore
                                    .getOptionName(project.fields, PROFILER_SEARCH_FIELDS.SITUATION)
                                    .join(', ')}
                            </div>
                          </div>
                        )}
                        {isProfilerAdvancedCriteriaSituationDisabled && (
                          <div className="field empty"></div>
                        )}
                      </div>
                      <OwnershipCriteriaGroup fields={project.fields} />
                    </>
                  )}
                </>
              )}
              {project.searchCategory ===
                UI_OPTIONS[UI_OPTION_KEYS.PROFILER_SEARCH_CATEGORY]['Industries'] && (
                <>
                  <div className="group-title">General</div>
                  <div className="group">
                    <div className="field full-width-desktop">
                      <div className="label">Industries</div>
                      <div className="value">
                        <ChipsList
                          list={utilsStore.getIndustryCodesDisplayNames(
                            project.fields[
                              PROFILER_INDUSTRY_CODES_SEARCH_FIELDS.INDUSTRY_CODES.NAME
                            ]
                          )}
                          visibleCount={state.layout === 'desktop' ? 88888 : 2}
                        />
                      </div>
                    </div>
                  </div>
                  <div className="group">
                    <div className="field">
                      <div className="label">
                        {PROFILER_INDUSTRY_CODES_SEARCH_FIELDS.REVENUE_MIN.LABEL}
                      </div>
                      <div className="value">
                        {formatNumberRange(
                          project.fields[PROFILER_INDUSTRY_CODES_SEARCH_FIELDS.REVENUE_MIN.NAME],
                          project.fields[PROFILER_INDUSTRY_CODES_SEARCH_FIELDS.REVENUE_MAX.NAME]
                        )}
                      </div>
                    </div>
                    <div className="field empty"></div>
                    <div className="field empty"></div>
                  </div>
                  <div className="group-title">
                    {PROFILER_INDUSTRY_CODES_SEARCH_CATEGORIES.ADVANCED_SEARCH_CRITERIA}
                  </div>
                  {isProfilerAdvancedCriteriaDisabled ? (
                    <>
                      <div className="group">
                        <div className="field">
                          <div className="label">
                            {PROFILER_INDUSTRY_CODES_SEARCH_FIELDS.EMPLOYEES_MIN.LABEL}
                          </div>
                          <div className="value">
                            {!project.fields[
                              PROFILER_INDUSTRY_CODES_SEARCH_FIELDS.EMPLOYEES_MIN.NAME
                            ]
                              ? ''
                              : formatNumberRange(
                                  project.fields[
                                    PROFILER_INDUSTRY_CODES_SEARCH_FIELDS.EMPLOYEES_MIN.NAME
                                  ],
                                  project.fields[
                                    PROFILER_INDUSTRY_CODES_SEARCH_FIELDS.EMPLOYEES_MAX.NAME
                                  ]
                                )}
                          </div>
                        </div>
                        <div className="field">
                          <div className="label">
                            {PROFILER_INDUSTRY_CODES_SEARCH_FIELDS.HEADQUARTER.LABEL}
                          </div>
                          <div className="value">
                            {!project.fields[PROFILER_INDUSTRY_CODES_SEARCH_FIELDS.HEADQUARTER.NAME]
                              .length
                              ? ''
                              : utilsStore
                                  .getOptionName(
                                    project.fields,
                                    PROFILER_INDUSTRY_CODES_SEARCH_FIELDS.HEADQUARTER
                                  )
                                  .join(', ')}
                          </div>
                        </div>
                        <div className="field empty"></div>
                      </div>
                    </>
                  ) : (
                    <>
                      <div className="group">
                        <div className="field">
                          <div className="label">
                            {PROFILER_INDUSTRY_CODES_SEARCH_FIELDS.CAGR_MIN.LABEL}
                          </div>
                          <div className="value">
                            {!project.fields[PROFILER_INDUSTRY_CODES_SEARCH_FIELDS.CAGR_MIN.NAME] &&
                            !project.fields[PROFILER_INDUSTRY_CODES_SEARCH_FIELDS.CAGR_MAX.NAME]
                              ? ''
                              : formatNumberRange(
                                  project.fields[
                                    PROFILER_INDUSTRY_CODES_SEARCH_FIELDS.CAGR_MIN.NAME
                                  ],
                                  project.fields[
                                    PROFILER_INDUSTRY_CODES_SEARCH_FIELDS.CAGR_MAX.NAME
                                  ],
                                  PUNCTUATION.EN_DASH,
                                  true
                                )}
                          </div>
                        </div>
                        <div className="field">
                          <div className="label">
                            {PROFILER_INDUSTRY_CODES_SEARCH_FIELDS.CAGR_PERIOD.LABEL}
                          </div>
                          <div className="value">
                            {!project.fields[PROFILER_INDUSTRY_CODES_SEARCH_FIELDS.CAGR_PERIOD.NAME]
                              .length
                              ? ''
                              : utilsStore.getOptionName(
                                  project.fields,
                                  PROFILER_INDUSTRY_CODES_SEARCH_FIELDS.CAGR_PERIOD
                                )}
                          </div>
                        </div>
                        <div className="field">
                          <div className="label">
                            {PROFILER_INDUSTRY_CODES_SEARCH_FIELDS.EBIT_MARGIN_MIN.LABEL}
                          </div>
                          <div className="value">
                            {!project.fields[
                              PROFILER_INDUSTRY_CODES_SEARCH_FIELDS.EBIT_MARGIN_MIN.NAME
                            ] &&
                            !project.fields[
                              PROFILER_INDUSTRY_CODES_SEARCH_FIELDS.EBIT_MARGIN_MAX.NAME
                            ]
                              ? ''
                              : formatNumberRange(
                                  project.fields[
                                    PROFILER_INDUSTRY_CODES_SEARCH_FIELDS.EBIT_MARGIN_MIN.NAME
                                  ],
                                  project.fields[
                                    PROFILER_INDUSTRY_CODES_SEARCH_FIELDS.EBIT_MARGIN_MAX.NAME
                                  ],
                                  PUNCTUATION.EN_DASH,
                                  true
                                )}
                          </div>
                        </div>
                      </div>
                      <div className="group">
                        <div className="field">
                          <div className="label">
                            {PROFILER_INDUSTRY_CODES_SEARCH_FIELDS.EMPLOYEES_MIN.LABEL}
                          </div>
                          <div className="value">
                            {!project.fields[
                              PROFILER_INDUSTRY_CODES_SEARCH_FIELDS.EMPLOYEES_MIN.NAME
                            ]
                              ? ''
                              : formatNumberRange(
                                  project.fields[
                                    PROFILER_INDUSTRY_CODES_SEARCH_FIELDS.EMPLOYEES_MIN.NAME
                                  ],
                                  project.fields[
                                    PROFILER_INDUSTRY_CODES_SEARCH_FIELDS.EMPLOYEES_MAX.NAME
                                  ]
                                )}
                          </div>
                        </div>
                        <div className="field">
                          <div className="label">
                            {PROFILER_INDUSTRY_CODES_SEARCH_FIELDS.HEADQUARTER.LABEL}
                          </div>
                          <div className="value">
                            {!project.fields[PROFILER_INDUSTRY_CODES_SEARCH_FIELDS.HEADQUARTER.NAME]
                              .length
                              ? ''
                              : utilsStore
                                  .getOptionName(
                                    project.fields,
                                    PROFILER_INDUSTRY_CODES_SEARCH_FIELDS.HEADQUARTER
                                  )
                                  .join(', ')}
                          </div>
                        </div>
                        {!isProfilerAdvancedCriteriaSituationDisabled && (
                          <div className="field">
                            <div className="label">
                              {PROFILER_INDUSTRY_CODES_SEARCH_FIELDS.SITUATION.LABEL}
                            </div>
                            <div className="value">
                              {!project.fields[PROFILER_INDUSTRY_CODES_SEARCH_FIELDS.SITUATION.NAME]
                                .length
                                ? ''
                                : utilsStore
                                    .getOptionName(
                                      project.fields,
                                      PROFILER_INDUSTRY_CODES_SEARCH_FIELDS.SITUATION
                                    )
                                    .join(', ')}
                            </div>
                          </div>
                        )}
                        {isProfilerAdvancedCriteriaSituationDisabled && (
                          <div className="field empty"></div>
                        )}
                      </div>
                      <OwnershipCriteriaGroup fields={project.fields} />
                    </>
                  )}
                </>
              )}
              {isBulkSearchProject(project.searchCategory) && (
                <div className="group">
                  <div className="field full-width-desktop">
                    <div className="value">{bulkSearchCriteriaText(project)}</div>
                  </div>
                </div>
              )}
            </div>
          )}
        </div>
      </section>

      <section className="lists">
        <div id="project-view-scroll-starting-point" />
        <div id="tab-before-after-helper" />
        {/* Harmonized toolbar anchor — the child results view portals its
            Add-filter / Customize / feedback / view / sort / info / download
            bar into this node so it sits ABOVE the tab strip (flag-on desktop).
            Carries `.table-responsive` because the toolbar's button + dropdown
            styling (base `.project-sort-wrapper` + the TD-2575 free-standing-row
            modifier) is scoped under `.table-responsive`; without it the portaled
            bar would render unstyled. Empty (zero-height) until the child mounts
            on a results tab. */}
        {isUnifiedFiltersEnabled && state.layout === 'desktop' && (
          <div ref={setToolbarMountNode} className="table-responsive pv-toolbar-portal-mount" />
        )}
        <div className="tabs at-project-results">
          {selectedProjectTabs.map((tab) => {
            return (
              <div
                id={tab.id}
                key={tab.name}
                onClick={tab.onClick}
                className={`tab ${tab.isActive ? 'active' : ''}`}
              >
                <ProjectTabLabelText name={tab.name} mobileName={tab.mobileName} />
              </div>
            );
          })}
        </div>

        {state.loadError && params.selectedProjectTab !== SELECTED_PROJECT_TABS.COMMENTS && (
          <GeneralError
            errorMessage={state.loadError}
            actionMessage="You may want to:"
            actionButtontext="Try again"
            onActionButtonClick={() => state.loadResults(project.id, project.searchCategory)}
          />
        )}

        {state.isLoading && params.selectedProjectTab !== SELECTED_PROJECT_TABS.COMMENTS && (
          <GeneralLoader />
        )}

        {!state.isLoading && !state.loadError && isResultsTableTab(params.selectedProjectTab) && (
          <ProfilerSearchResultsTabView
            key={project.id}
            project={project}
            results={state.results}
            forTab={params.selectedProjectTab}
            params={params}
            excludeResult={state.excludeResult}
            entriesInAction={state.entriesInAction}
            displayDetails={navStack.displayDetails}
            isFromAgentView={isFromAgentView}
            fullResultsCount={state.fullResultsCount}
            onFilteredCountChange={state.setFilteredResultsCount}
            feedbackSlot={harmonizedFeedbackSlot}
            trailingActionsSlot={harmonizedTrailingActionsSlot}
            mobilePrimaryAction={mobilePrimaryAction}
            mobileMoreActions={mobileMoreActions}
            onBulkExclude={state.bulkExcludeResults}
            toolbarMountNode={toolbarMountNode}
          />
        )}

        {params.selectedProjectTab === SELECTED_PROJECT_TABS.COMMENTS && (
          <ProfilerCommentsTabView params={params} project={project} />
        )}
      </section>
      {/* TD-2769 — on results tabs the mobile bar (View / Sort / Customize + these
          project-level controls) is rendered by the results child, which owns the
          toolbar store — but ONLY once results are in: the child mounts on
          `!isLoading && !loadError && isResultsTableTab`. So the parent renders the
          project-level bar whenever the child does NOT: on non-results tabs
          (Comments), AND while a results tab is still loading or has errored —
          otherwise Download / feedback / data-source vanish during every load and
          permanently on a load error (they were unconditional in Phase-1). The
          conditions stay mutually exclusive, so there is never a double bar. */}
      {(!isResultsTableTab(params.selectedProjectTab) || state.isLoading || state.loadError) && (
        <ResultsMobileActionBar
          primary={mobilePrimaryAction}
          moreActions={mobileMoreActions}
          ariaLabel="Project results actions"
          breakpoint="to-1200"
        />
      )}
      <ProjectDownloadOverlays downloads={downloads} isMobile={state.layout === 'mobile'} />
      {showFeedbackPopup && (
        <FeedbackPopup
          toggle={state.toggleRequestFeedbackPopup}
          companyUsers={authStore.companyUsers}
          projectId={params.selectedProjectId}
        />
      )}
    </div>
  );
});

export default ProfilerProjectView;
